import React, { useState, useEffect, useMemo } from 'react';
import { Copy, Clipboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Doctor, Staff, Room, ScheduleItem } from './SchedulerTypes';
import { ViewType } from './Scheduler';
import DoctorQuickReportModal from './DoctorQuickReportModal';
import StaffQuickReportModal from './StaffQuickReportModal';
import ScheduleModal from './ScheduleModal';
import { Reassignment } from './ConflictResolutionModal';
import { getWeekDays, formatDateToString, isToday, toSingaporeTime, expandMultiDaySchedules, getCurrentSingaporeDate } from '../lib/dateUtils';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from './LoadingOverlay';
import PlatoSyncButton from './PlatoSyncButton';
import { useClinic } from '@/contexts/ClinicContext';

interface WeeklyViewProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  onViewChange: (view: ViewType) => void;
  scheduleItems: ScheduleItem[];
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
  initialTab?: string;
  onAddScheduleItem: (item: Omit<ScheduleItem, 'id'>) => void;
  onUpdateScheduleItem: (id: string, updates: Partial<ScheduleItem>) => void;
  onDeleteScheduleItem: (id: string) => void;
  onDeleteScheduleSeries?: (ids: string[]) => void;
  onCopyScheduleItems: (date: string) => void;
  onPasteScheduleItems: (date: string) => void;
  copiedItems: ScheduleItem[];
  isLoadingSchedules?: boolean;
  isFetchingSchedules?: boolean;
  onRefreshSchedule?: () => void;
}

export default function WeeklyView({
  currentDate,
  onDateChange,
  onViewChange,
  scheduleItems,
  doctors,
  staff,
  rooms,
  initialTab,
  onAddScheduleItem,
  onUpdateScheduleItem,
  onDeleteScheduleItem,
  onDeleteScheduleSeries,
  onCopyScheduleItems,
  onPasteScheduleItems,
  copiedItems,
  isLoadingSchedules = false,
  isFetchingSchedules = false,
  onRefreshSchedule,
}: WeeklyViewProps) {
  const navigate = useNavigate();
  const { currentClinic } = useClinic();
  
  // Map URL-friendly tab names to actual tab values
  const tabMapping: { [key: string]: string } = {
    'all-schedule': 'all',
    'doctors': 'doctors',
    'staff': 'staff'
  };
  
  // Determine the default tab from the URL or props
  const getDefaultTab = () => {
    if (initialTab && tabMapping[initialTab]) {
      return tabMapping[initialTab];
    }
    return 'all';
  };
  
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [isTabLoading, setIsTabLoading] = useState(false);

  // Update tab when URL changes
  useEffect(() => {
    if (initialTab && tabMapping[initialTab]) {
      setActiveTab(tabMapping[initialTab]);
    }
  }, [initialTab]);

  // Handle tab change
  const handleTabChange = (value: string) => {
    setIsTabLoading(true);
    setActiveTab(value);
    // Update URL with the new tab
    const reverseMapping: { [key: string]: string } = {
      'all': 'all-schedule',
      'doctors': 'doctors',
      'staff': 'staff'
    };
    navigate(`/weekly/${reverseMapping[value]}`);

    // Clear loading after minimum display time
    setTimeout(() => setIsTabLoading(false), 300);
  };
  const [doctorModalState, setDoctorModalState] = useState<{
    isOpen: boolean;
    scheduleItem: ScheduleItem | null;
    doctor: Doctor | null;
    staff: Staff | null;
    room: Room | null;
    allSchedulesForDay?: ScheduleItem[];
  }>({
    isOpen: false,
    scheduleItem: null,
    doctor: null,
    staff: null,
    room: null,
    allSchedulesForDay: []
  });

  const [staffModalState, setStaffModalState] = useState<{
    isOpen: boolean;
    scheduleItem: ScheduleItem | null;
    staff: Staff | null;
    doctor: Doctor | null;
    room: Room | null;
    allSchedulesForDay?: ScheduleItem[];
  }>({
    isOpen: false,
    scheduleItem: null,
    staff: null,
    doctor: null,
    room: null,
    allSchedulesForDay: []
  });

  // Helper functions
  const getDoctorById = (doctorId: string) => doctors.find(d => d.id === doctorId);
  const getStaffById = (staffId: string) => staff.find(s => s.id === staffId);
  const getRoomById = (roomId: string) => rooms.find(r => r.id === roomId);

  const handleDoctorClick = (scheduleItem: ScheduleItem, e: React.MouseEvent, allSchedulesForDay?: ScheduleItem[]) => {
    e.stopPropagation();
    
    const doctor = scheduleItem.doctorId ? getDoctorById(scheduleItem.doctorId) : null;
    const staffMember = scheduleItem.staffId ? getStaffById(scheduleItem.staffId) : null;
    const room = scheduleItem.roomId ? getRoomById(scheduleItem.roomId) : null;

    if (doctor) {
      setDoctorModalState({
        isOpen: true,
        scheduleItem,
        doctor,
        staff: staffMember || null,
        room: room || null,
        allSchedulesForDay: allSchedulesForDay || [scheduleItem]
      });
    }
  };

  const handleStaffClick = (scheduleItem: ScheduleItem, e: React.MouseEvent, allSchedulesForDay?: ScheduleItem[]) => {
    e.stopPropagation();
    
    const staffMember = scheduleItem.staffId ? getStaffById(scheduleItem.staffId) : null;
    const doctor = scheduleItem.doctorId ? getDoctorById(scheduleItem.doctorId) : null;
    const room = scheduleItem.roomId ? getRoomById(scheduleItem.roomId) : null;

    if (staffMember) {
      setStaffModalState({
        isOpen: true,
        scheduleItem,
        staff: staffMember,
        doctor: doctor || null,
        room: room || null,
        allSchedulesForDay: allSchedulesForDay || [scheduleItem]
      });
    }
  };

  const closeDoctorModal = () => {
    setDoctorModalState({
      isOpen: false,
      scheduleItem: null,
      doctor: null,
      staff: null,
      room: null,
      allSchedulesForDay: []
    });
  };

  const closeStaffModal = () => {
    setStaffModalState({
      isOpen: false,
      scheduleItem: null,
      staff: null,
      doctor: null,
      room: null,
      allSchedulesForDay: []
    });
  };

  const handleDeleteSchedule = (scheduleId: string) => {
    // Extract base UUID from composite IDs (removes _day_X suffix)
    const baseId = scheduleId.split('_day_')[0];
    onDeleteScheduleItem(baseId);
    closeDoctorModal();
    closeStaffModal();
  };

  const handleDeleteSeries = (ids: string[]) => {
    onDeleteScheduleSeries?.(ids);
    closeDoctorModal();
    closeStaffModal();
  };

  // Schedule Modal state
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [editingScheduleItem, setEditingScheduleItem] = useState<ScheduleItem | null>(null);

  // ScheduleData interface for type safety
  interface ScheduleData {
    title: string;
    type: 'appointment' | 'admin' | 'break' | 'leave' | 'mc';
    doctorId?: string;
    staffId?: string;
    roomId?: string;
    startTime: string;
    endTime: string;
    notes?: string;
    date: string;
    endDate?: string;
  }

  // Schedule Modal handlers
  const openScheduleModal = (item?: ScheduleItem) => {
    setEditingScheduleItem(item || null);
    setIsScheduleModalOpen(true);
  };

  const closeScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setEditingScheduleItem(null);
  };

  const handleSaveSchedule = (scheduleData: ScheduleData, reassignments?: Reassignment[]) => {
    if (editingScheduleItem) {
      // Update existing schedule item - extract base UUID from composite IDs
      const baseId = editingScheduleItem.id.split('_day_')[0];
      onUpdateScheduleItem(baseId, scheduleData);
    } else {
      // Create new schedule item with today's date as default, potentially with reassignments
      const newScheduleData = {
        ...scheduleData,
        date: scheduleData.date || formatDateToString(getCurrentSingaporeDate())
      };
      onAddScheduleItem(newScheduleData);
    }
    closeScheduleModal();
  };

  const handleEditSchedule = (scheduleItem: ScheduleItem) => {
    // Extract the base UUID from composite IDs (removes _day_X suffix)
    const baseId = scheduleItem.id.split('_day_')[0];
    
    // Find the original schedule item using the base ID
    const originalItem = scheduleItems.find(item => item.id === baseId) || scheduleItem;
    
    // Open the schedule modal with the original item for editing
    openScheduleModal(originalItem);
  };

  const handleDayClick = (day: Date) => {
    onDateChange(day);
    onViewChange('daily');
  };

  const weekDays = getWeekDays(currentDate);
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Expand multi-day schedules to show on all applicable days
  const expandedScheduleItems = useMemo(() => {
    const weekStart = formatDateToString(weekDays[0]);
    const weekEnd = formatDateToString(weekDays[6]);
    return expandMultiDaySchedules(scheduleItems, weekStart, weekEnd);
  }, [scheduleItems, weekDays]);

  const renderWeeklyContent = (viewType: 'all' | 'doctors' | 'staff') => (
    <div className="overflow-x-auto -mx-2 px-2" style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="min-w-[500px]">
      {/* Sticky day headers */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2 sticky top-0 z-30 bg-background pb-2 mb-2 border-b">
        {weekDays.map((day, index) => {
          const isTodayDate = isToday(day);
          return (
            <div key={index} className={`p-1.5 sm:p-2 text-center font-medium text-xs sm:text-sm ${isTodayDate ? 'text-primary bg-primary/10' : 'text-muted-foreground bg-muted/50'}`}>
              <span className="hidden sm:inline">{dayNames[index]}</span>
              <span className="sm:hidden">{dayNames[index].slice(0, 3)}</span>
              <div className="text-xs sm:text-sm">{format(day, 'd')}</div>
            </div>
          );
        })}
      </div>
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
      {weekDays.map((day, index) => {
        const dateString = formatDateToString(day);
        let daySchedules: ScheduleItem[] = [];
        
        // Filter schedules based on view type
        if (viewType === 'all') {
          daySchedules = expandedScheduleItems.filter(item => item.date === dateString);
        } else if (viewType === 'doctors') {
          daySchedules = expandedScheduleItems.filter(item => item.date === dateString && item.doctorId);
        } else if (viewType === 'staff') {
          daySchedules = expandedScheduleItems.filter(item => item.date === dateString && item.staffId);
        }
        
        // Sort items so Leave/MC appears at the bottom
        daySchedules.sort((a, b) => {
          const aIsLeave = a.type === 'leave' || a.type === 'mc';
          const bIsLeave = b.type === 'leave' || b.type === 'mc';
          
          if (aIsLeave && !bIsLeave) return 1;
          if (!aIsLeave && bIsLeave) return -1;
          return 0;
        });
        
        const isTodayDate = isToday(day);

        return (
          <Card
            key={index}
            className={`min-h-[150px] sm:min-h-[300px] cursor-pointer transition-colors hover:bg-muted/50 overflow-visible ${
              isTodayDate ? 'ring-2 ring-primary' : ''
            }`}
            onClick={() => handleDayClick(day)}
          >
            <CardHeader className="p-3 bg-background border-b">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {daySchedules.length} schedule{daySchedules.length !== 1 ? 's' : ''}
                </div>
                <div className="flex space-x-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopyScheduleItems(dateString);
                    }}
                    title="Copy day schedule"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  {copiedItems.length > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPasteScheduleItems(dateString);
                      }}
                      title={`Paste ${copiedItems.length} items`}
                    >
                      <Clipboard className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              <div className="space-y-1">
                {viewType === 'all' && (() => {
                  // Group schedules by person (doctor or staff) to show each person only once
                  const personGroups = daySchedules.reduce((groups, item) => {
                    const personId = item.doctorId || item.staffId || 'unknown';
                    if (!groups[personId]) {
                      groups[personId] = [];
                    }
                    groups[personId].push(item);
                    return groups;
                  }, {} as Record<string, ScheduleItem[]>);

                  const uniquePersons = Object.keys(personGroups);

                  // Sort persons so those with Leave/MC appear at the bottom
                  uniquePersons.sort((a, b) => {
                    const aHasLeave = personGroups[a].some(item => item.type === 'leave' || item.type === 'mc');
                    const bHasLeave = personGroups[b].some(item => item.type === 'leave' || item.type === 'mc');

                    if (aHasLeave && !bHasLeave) return 1;
                    if (!aHasLeave && bHasLeave) return -1;
                    return 0;
                  });

                  return uniquePersons.map(personId => {
                    const personSchedulesForDay = personGroups[personId];
                    const firstItem = personSchedulesForDay[0];
                    const doctor = firstItem.doctorId ? getDoctorById(firstItem.doctorId) : null;
                    const staffMember = firstItem.staffId ? getStaffById(firstItem.staffId) : null;
                    const person = doctor || staffMember;

                    if (!person) return null;

                    // Check if any of the person's schedules are leave/MC
                    const hasLeave = personSchedulesForDay.some(item =>
                      item.type === 'leave' || item.type === 'mc'
                    );
                    const hasMC = personSchedulesForDay.some(item => item.type === 'mc');

                    return (
                      <div
                        key={personId}
                        className={`text-xs p-2 rounded border mb-1 cursor-pointer hover:opacity-80 transition-opacity ${
                          hasMC
                            ? 'bg-red-700 border-red-800 text-white'
                            : hasLeave
                            ? 'bg-red-500 border-red-600 text-white'
                            : doctor ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-green-50 border-green-200 text-green-800'
                        }`}
                        style={{
                          backgroundColor: hasMC ? '#b91c1c' : hasLeave ? '#ef4444' : `${person.color}20`,
                          borderColor: hasMC ? '#991b1b' : hasLeave ? '#dc2626' : person.color,
                          color: hasMC ? '#ffffff' : hasLeave ? '#ffffff' : person.color
                        }}
                        onClick={(e) => doctor ? handleDoctorClick(firstItem, e, personSchedulesForDay) : handleStaffClick(firstItem, e, personSchedulesForDay)}
                      >
                        <div className="font-medium flex items-center justify-between">
                          <span>{person.short_name || person.name}</span>
                          {personSchedulesForDay.length > 1 && (
                            <span className="opacity-70">
                              ({personSchedulesForDay.length})
                            </span>
                          )}
                        </div>
                        <div className="text-muted-foreground">
                          {personSchedulesForDay.length === 1
                            ? `${firstItem.startTime} - ${firstItem.endTime}`
                            : `${personSchedulesForDay.length} schedules`
                          }
                        </div>
                      </div>
                    );
                  });
                })()}
                
                {viewType === 'doctors' && (() => {
                  // Group schedules by doctor to show each doctor only once
                  const doctorGroups = daySchedules.reduce((groups, item) => {
                    if (item.doctorId) {
                      const doctorId = item.doctorId;
                      if (!groups[doctorId]) {
                        groups[doctorId] = [];
                      }
                      groups[doctorId].push(item);
                    }
                    return groups;
                  }, {} as Record<string, ScheduleItem[]>);
                  
                  const uniqueDoctors = Object.keys(doctorGroups);
                  
                  // Sort doctors so those with Leave/MC appear at the bottom
                  uniqueDoctors.sort((a, b) => {
                    const aHasLeave = doctorGroups[a].some(item => item.type === 'leave' || item.type === 'mc');
                    const bHasLeave = doctorGroups[b].some(item => item.type === 'leave' || item.type === 'mc');
                    
                    if (aHasLeave && !bHasLeave) return 1;
                    if (!aHasLeave && bHasLeave) return -1;
                    return 0;
                  });
                  
                  return uniqueDoctors.map(doctorId => {
                    const doctor = getDoctorById(doctorId);
                    const doctorSchedulesForDay = doctorGroups[doctorId];
                    
                    // Check if any of the doctor's schedules are leave/MC
                    const hasLeave = doctorSchedulesForDay.some(item => 
                      item.type === 'leave' || item.type === 'mc'
                    );
                    const hasMC = doctorSchedulesForDay.some(item => item.type === 'mc');
                    
                    // Use the first schedule item for the click handler (will show all in modal)
                    const primarySchedule = doctorSchedulesForDay[0];
                    
                    if (!doctor) return null;
                    
                    return (
                      <div
                        key={doctorId}
                        className={`text-xs p-2 rounded border mb-1 cursor-pointer hover:opacity-80 transition-opacity ${
                          hasMC
                            ? 'bg-red-700 border-red-800 text-white'
                            : hasLeave 
                            ? 'bg-red-500 border-red-600 text-white' 
                            : 'bg-blue-50 border-blue-200 text-blue-800'
                        }`}
                        style={{ 
                          backgroundColor: hasMC ? '#b91c1c' : hasLeave ? '#ef4444' : `${doctor.color}20`, 
                          borderColor: hasMC ? '#991b1b' : hasLeave ? '#dc2626' : doctor.color,
                          color: hasMC ? '#ffffff' : hasLeave ? '#ffffff' : doctor.color
                        }}
                        onClick={(e) => handleDoctorClick(primarySchedule, e, doctorSchedulesForDay)}
                      >
                        <div className="font-medium flex items-center justify-between">
                          <span>{doctor.short_name || doctor.name}</span>
                          {doctorSchedulesForDay.length > 1 && (
                            <span className="opacity-70">
                              ({doctorSchedulesForDay.length})
                            </span>
                          )}
                        </div>
                        <div className="text-muted-foreground">
                          {doctorSchedulesForDay.length === 1 
                            ? `${primarySchedule.startTime} - ${primarySchedule.endTime}`
                            : `${doctorSchedulesForDay.length} schedules`
                          }
                        </div>
                      </div>
                    );
                  });
                })()}
                
                {viewType === 'staff' && (() => {
                  // Group schedules by staff to show each staff only once
                  const staffGroups = daySchedules.reduce((groups, item) => {
                    if (item.staffId) {
                      const staffId = item.staffId;
                      if (!groups[staffId]) {
                        groups[staffId] = [];
                      }
                      groups[staffId].push(item);
                    }
                    return groups;
                  }, {} as Record<string, ScheduleItem[]>);
                  
                  const uniqueStaff = Object.keys(staffGroups);
                  
                  // Sort staff so those with Leave/MC appear at the bottom
                  uniqueStaff.sort((a, b) => {
                    const aHasLeave = staffGroups[a].some(item => item.type === 'leave' || item.type === 'mc');
                    const bHasLeave = staffGroups[b].some(item => item.type === 'leave' || item.type === 'mc');
                    
                    if (aHasLeave && !bHasLeave) return 1;
                    if (!aHasLeave && bHasLeave) return -1;
                    return 0;
                  });
                  
                  return uniqueStaff.map(staffId => {
                    const staffMember = getStaffById(staffId);
                    const staffSchedulesForDay = staffGroups[staffId];
                    
                    // Check if any of the staff's schedules are leave/MC
                    const hasLeave = staffSchedulesForDay.some(item => 
                      item.type === 'leave' || item.type === 'mc'
                    );
                    const hasMC = staffSchedulesForDay.some(item => item.type === 'mc');
                    
                    // Use the first schedule item for the click handler (will show all in modal)
                    const primarySchedule = staffSchedulesForDay[0];
                    
                    if (!staffMember) return null;
                    
                    return (
                      <div
                        key={staffId}
                        className={`text-xs p-2 rounded border mb-1 cursor-pointer hover:opacity-80 transition-opacity ${
                          hasMC
                            ? 'bg-red-700 border-red-800 text-white'
                            : hasLeave 
                            ? 'bg-red-500 border-red-600 text-white' 
                            : 'bg-green-50 border-green-200 text-green-800'
                        }`}
                        style={{ 
                          backgroundColor: hasMC ? '#b91c1c' : hasLeave ? '#ef4444' : `${staffMember.color}20`, 
                          borderColor: hasMC ? '#991b1b' : hasLeave ? '#dc2626' : staffMember.color,
                          color: hasMC ? '#ffffff' : hasLeave ? '#ffffff' : staffMember.color
                        }}
                        onClick={(e) => handleStaffClick(primarySchedule, e, staffSchedulesForDay)}
                      >
                        <div className="font-medium flex items-center justify-between">
                          <span>{staffMember.short_name || staffMember.name}</span>
                          {staffSchedulesForDay.length > 1 && (
                            <span className="opacity-70">
                              ({staffSchedulesForDay.length})
                            </span>
                          )}
                        </div>
                        <div className="text-muted-foreground">
                          {staffSchedulesForDay.length === 1 
                            ? `${primarySchedule.startTime} - ${primarySchedule.endTime}`
                            : `${staffSchedulesForDay.length} schedules`
                          }
                        </div>
                      </div>
                    );
                  });
                })()}
                
                {daySchedules.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No {viewType === 'all' ? '' : viewType} schedules
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
      </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg sm:text-xl font-semibold">
          Week of {toSingaporeTime(weekDays[0]).toLocaleDateString('en-SG')} - {toSingaporeTime(weekDays[6]).toLocaleDateString('en-SG')}
        </h2>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const prevWeek = new Date(currentDate);
              prevWeek.setDate(currentDate.getDate() - 7);
              onDateChange(prevWeek);
            }}
          >
            <span className="hidden sm:inline">Previous Week</span>
            <span className="sm:hidden">Prev</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const nextWeek = new Date(currentDate);
              nextWeek.setDate(currentDate.getDate() + 7);
              onDateChange(nextWeek);
            }}
          >
            <span className="hidden sm:inline">Next Week</span>
            <span className="sm:hidden">Next</span>
          </Button>
          {/* Plato Sync Button - Only show for Forum clinic */}
          {currentClinic?.code?.toLowerCase() === 'forum' && (
            <PlatoSyncButton
              syncType="week"
              date={formatDateToString(weekDays[0])}
              clinicId={currentClinic.id}
              onRefreshSchedule={onRefreshSchedule}
            />
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div className="overflow-x-auto -mx-2 px-2 pb-1">
            <TabsList className="grid w-fit min-w-full sm:min-w-0 sm:w-fit grid-cols-3">
              <TabsTrigger value="all" className="text-xs sm:text-sm whitespace-nowrap">All Schedules</TabsTrigger>
              <TabsTrigger value="doctors" className="text-xs sm:text-sm whitespace-nowrap">Doctor View</TabsTrigger>
              <TabsTrigger value="staff" className="text-xs sm:text-sm whitespace-nowrap">Staff View</TabsTrigger>
            </TabsList>
          </div>

          <div className="flex items-center space-x-2 overflow-x-auto pb-1">
            <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs shrink-0">Doctors</Badge>
            <Badge variant="outline" className="bg-green-50 text-green-700 text-xs shrink-0">Staff</Badge>
            <Badge variant="outline" className="bg-red-700 text-white border-red-700 text-xs shrink-0">Leave/MC</Badge>
          </div>
        </div>

        <TabsContent value="all">
          {renderWeeklyContent('all')}
        </TabsContent>

        <TabsContent value="doctors">
          {renderWeeklyContent('doctors')}
        </TabsContent>

        <TabsContent value="staff">
          {renderWeeklyContent('staff')}
        </TabsContent>
      </Tabs>

      {/* Doctor Quick Report Modal */}
      <DoctorQuickReportModal
        isOpen={doctorModalState.isOpen}
        onClose={closeDoctorModal}
        scheduleItem={doctorModalState.scheduleItem}
        doctor={doctorModalState.doctor}
        staff={doctorModalState.staff}
        room={doctorModalState.room}
        onDeleteSchedule={handleDeleteSchedule}
        onEditSchedule={handleEditSchedule}
        allSchedulesForDay={doctorModalState.allSchedulesForDay}
        allStaff={staff}
        allRooms={rooms}
      />

      {/* Staff Quick Report Modal */}
      <StaffQuickReportModal
        isOpen={staffModalState.isOpen}
        onClose={closeStaffModal}
        scheduleItem={staffModalState.scheduleItem}
        staff={staffModalState.staff}
        doctor={staffModalState.doctor}
        room={staffModalState.room}
        onDeleteSchedule={handleDeleteSchedule}
        onEditSchedule={handleEditSchedule}
        allSchedulesForDay={staffModalState.allSchedulesForDay}
        allDoctors={doctors}
        allRooms={rooms}
      />

      {/* Schedule Modal */}
      <ScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={closeScheduleModal}
        onSave={handleSaveSchedule}
        onDelete={handleDeleteSchedule}
        onDeleteSeries={handleDeleteSeries}
        onCreateAppointment={() => {}} // Empty handler for now
        doctors={doctors}
        staff={staff}
        rooms={rooms}
        initialData={editingScheduleItem}
        defaultStartTime="09:00"
        currentDate={getCurrentSingaporeDate()}
        scheduleItems={scheduleItems}
      />

      {/* Loading Overlay */}
      <LoadingOverlay
        isLoading={isLoadingSchedules || isTabLoading}
        message={isTabLoading ? "Switching view..." : "Loading weekly schedule..."}
      />
    </div>
  );
}
